/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { CARD_TYPES, BAR_CHART_TYPES } from 'carbon-addons-iot-react';
import moment, { Moment } from 'moment';

import { COLUMNTYPE_TIMESTAMP, INTERVALS, TIME_GRAINS } from '../constants/constants';
import {
  PALTimeRange,
  TimeRanges,
  PickerKinds,
  TimeRangeOption,
  PALAbsoluteTimeRangeValue,
  PALRelativeTimeRangeValue,
  PALPresetTimeRange,
} from '../types/DateTypes';
import { CardTypes, DataSource, Range, Card } from '../../Dashboard/types/dashboardTypes';

/**
 * Initializes the initialZoomDomain to the current endDate back for 500 points
 * @param {Array<Number>} allTimestamps all timestamps
 * @return {Array<Number>} initialZoomDomain
 */
export const setInitialZoomDomain = (allTimestamps: number[]): number[] => {
  const startDate =
    allTimestamps.length > 500 ? allTimestamps[allTimestamps.length - 500] : allTimestamps[0];
  return [startDate, allTimestamps[allTimestamps.length - 1]];
};

export const timeRangeToDataSourceRangeMap = {
  lastHour: { interval: INTERVALS.HOUR, count: -1, type: 'rolling' },
  last2Hours: { interval: INTERVALS.HOUR, count: -2, type: 'rolling' },
  last4Hours: { interval: INTERVALS.HOUR, count: -4, type: 'rolling' },
  last8Hours: { interval: INTERVALS.HOUR, count: -8, type: 'rolling' },
  last24Hours: { interval: INTERVALS.HOUR, count: -24, type: 'rolling' },
  last7Days: { interval: INTERVALS.DAY, count: -7, type: 'rolling' },
  lastMonth: { interval: INTERVALS.MONTH, count: -1, type: 'rolling' },
  lastQuarter: {
    interval: INTERVALS.QUARTER,
    count: -1,
    type: 'rolling',
  },
  lastYear: {
    interval: INTERVALS.YEAR,
    count: -1,
    type: 'rolling',
  },
  this24Hours: { interval: INTERVALS.HOUR, count: -24, type: 'periodToDate' },
  thisWeek: {
    interval: INTERVALS.WEEK,
    count: -1,
    type: 'periodToDate',
  },
  thisMonth: {
    interval: INTERVALS.MONTH,
    count: -1,
    type: 'periodToDate',
  },
  thisQuarter: {
    interval: INTERVALS.QUARTER,
    count: -1,
    type: 'periodToDate',
  },
  thisYear: {
    interval: INTERVALS.YEAR,
    count: -1,
    type: 'periodToDate',
  },
};

/** returns end date for card without seconds or milliseconds */
export const generateEndDate = (): Moment => moment().set('second', 0).set('millisecond', 0);

/**
 * Based on the string card range, this function returns the proper card.dataSource
 * which is primarily used by the APIs
 * @param {String} timeGrain the card timeGrain needed because some card types don't have timeGrains
 * @param {String} timeRange, one of the selected time range settings from the card dropdown
 * @param {String} type the card tyep
 * @returns {Object} { range: {interval: {string}, count: {number}, type: {string}}, timeGrain: {string} }
 */
export const determineDataSourceBasedOnSelectedTimeRange = (
  timeGrain: string,
  timeRange: string,
  type: CardTypes
): DataSource => {
  let calculatedDataSource: DataSource;
  switch (timeRange) {
    case 'lastHour':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.lastHour,
        timeGrain: TIME_GRAINS.HOUR,
      };
      break;
    case 'last2Hours':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.last2Hours,
        timeGrain: TIME_GRAINS.HOUR,
      };
      break;
    case 'last4Hours':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.last4Hours,
        timeGrain: TIME_GRAINS.HOUR,
      };
      break;
    case 'last8Hours':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.last8Hours,
        timeGrain: TIME_GRAINS.HOUR,
      };
      break;
    case 'last24Hours':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.last24Hours,
        timeGrain: TIME_GRAINS.HOUR,
      };
      break;
    case 'last7Days':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.last7Days,
        timeGrain: TIME_GRAINS.DAY,
      };
      break;
    case 'lastMonth':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.lastMonth,
        timeGrain: TIME_GRAINS.DAY,
      };
      break;
    case 'lastQuarter':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.lastQuarter,
        timeGrain: TIME_GRAINS.WEEK,
      };
      break;
    case 'lastYear':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.lastYear,
        timeGrain: TIME_GRAINS.MONTH,
      };
      break;
    case 'this24Hours':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.this24Hours,
        timeGrain: TIME_GRAINS.HOUR,
      };
      break;
    case 'thisWeek':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.thisWeek,
        timeGrain: TIME_GRAINS.DAY,
      };
      break;
    case 'thisMonth':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.thisMonth,
        timeGrain: TIME_GRAINS.DAY,
      };
      break;
    case 'thisQuarter':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.thisQuarter,
        timeGrain: TIME_GRAINS.WEEK,
      };
      break;
    case 'thisYear':
      calculatedDataSource = {
        range: timeRangeToDataSourceRangeMap.thisYear,
        timeGrain: TIME_GRAINS.MONTH,
      };
      break;
    default:
      // if no time range is set do not change the data source
      break;
  }
  return {
    ...calculatedDataSource,
    timeGrain:
      timeGrain || type === CARD_TYPES.TIMESERIES // Value cards and Image cards don't have timegrains
        ? calculatedDataSource?.timeGrain
        : undefined,
  };
};

/**
 * Compare grains to decide which is greater
 * @param {String} grain1
 * @param {String} grain2
 * @returns {Number}
 */
export const compareGrains = (grain1: string, grain2: string): number => {
  const greaterGrains = {
    hour: [TIME_GRAINS.DAY, TIME_GRAINS.WEEK, TIME_GRAINS.MONTH, TIME_GRAINS.YEAR],
    day: [TIME_GRAINS.WEEK, TIME_GRAINS.MONTH, TIME_GRAINS.YEAR],
    week: [TIME_GRAINS.MONTH, TIME_GRAINS.YEAR],
    month: [TIME_GRAINS.YEAR],
    year: [],
  };
  if (grain1 === grain2) {
    return 0;
  }

  if (!grain1 || greaterGrains[grain1].includes(grain2)) {
    return -1;
  }
  return 1;
};

/**
 * Determines the start date of data for a given card
 *
 * The card will have a range if set in the JSON OR if the dropdown is used to change the range.
 * If the range is set in the card but the dashboard grain is changed to larger grain, we should expand the range
 *
 * @param {Object} range: the default range object from the card
 * @param {Number} range.count
 * @param {string} range.interval
 * @param {Object<moment>} endDate
 * @returns {Object<moment>}
 * */
export const determineStartDate = (range: Range, endDate: moment.Moment): moment.Moment => {
  // If no timegrain is specified at the dashboard level (instance dashboard) use the card grain
  // If the dashboardTimeGrain is less than the cardGrain use the default range

  if (range.type === 'periodToDate') {
    // If we've requested period to date which means go to the exact interval back
    // tslint:disable-next-line
    return moment(endDate).startOf(range.interval as moment.unitOfTime.StartOf);
  }
  // By default assume its rolling
  return moment(endDate).subtract(
    -1 * range.count,
    range.interval as moment.unitOfTime.DurationConstructor
  );
};

/** Determine the valid time range options for a card, returns the timeRangeOptions in a keyed object */
export const determineValidTimeRangesForCard = (card: Card): Record<string, string> => {
  const { i18n } = card;

  return {
    last24Hours: i18n.last24HoursLabel,
    last7Days: i18n.last7DaysLabel,
    lastMonth: i18n.lastMonthLabel,
    lastQuarter: i18n.lastQuarterLabel,
    lastYear: i18n.lastYearLabel,
    thisWeek: i18n.thisWeekLabel,
    thisMonth: i18n.thisMonthLabel,
    thisQuarter: i18n.thisQuarterLabel,
    thisYear: i18n.thisYearLabel,
  };
};

/** Determind whether a card displays time based data */
export const isTimeBasedCard = (card: Card): boolean =>
  Boolean(
    card.type === CARD_TYPES.TIMESERIES ||
      (card.type === CARD_TYPES.TABLE &&
        card.content?.columns?.find(column => column.type === COLUMNTYPE_TIMESTAMP)) ||
      (card.content?.type === BAR_CHART_TYPES.STACKED && card.content?.timeDataSourceId) ||
      (card.content?.type === BAR_CHART_TYPES.SIMPLE && card.content?.timeDataSourceId)
  );

/**
 * Check if the card should listen to the dashboard
 * @param range JSON Range of the card with intervals
 * @param cardTimeRange the JSON timeRange of the card
 * @returns
 */
export const shouldCardIgnoreDashboard = (range: Range, cardTimeRange: string): boolean =>
  // if the card has defined JSON ranges, the card is ignoring the dashboard
  Boolean(range || cardTimeRange);

export const determineStartDateFromPresetRangeChange = (
  endDate: moment.Moment,
  timeRangeId: TimeRanges,
  offset?: number
): moment.Moment => {
  let startDate = endDate.clone().subtract(7, 'days');

  switch (timeRangeId) {
    case TimeRanges.DEFAULT:
      if (offset) {
        // if we pass an offset use that
        startDate = endDate.clone().subtract(offset, 'minutes');
      }
      break;
    case TimeRanges.LAST_30_MINUTES:
      startDate = endDate.clone().subtract(30, 'minutes');
      break;
    case TimeRanges.LAST_HOUR:
      startDate = endDate.clone().subtract(1, 'hour');
      break;
    case TimeRanges.LAST_6_HOURS:
      startDate = endDate.clone().subtract(6, 'hours');
      break;
    case TimeRanges.LAST_12_HOURS:
      startDate = endDate.clone().subtract(12, 'hours');
      break;
    case TimeRanges.LAST_24_HOURS:
      startDate = endDate.clone().subtract(1, 'day');
      break;
    case TimeRanges.LAST_7_DAYS:
      startDate = endDate.clone().subtract(7, 'days');
      break;
    case TimeRanges.LAST_MONTH:
      startDate = endDate.clone().subtract(1, 'month');
      break;
    case TimeRanges.LAST_QUARTER:
      startDate = endDate.clone().subtract(1, 'quarter');
      break;
    case TimeRanges.LAST_YEAR:
      startDate = endDate.clone().subtract(1, 'year');
      break;
    case TimeRanges.LAST_5_YEARS:
      startDate = endDate.clone().subtract(5, 'year');
      break;
    default:
      break;
  }
  return startDate;
};

export const determineDatesFromTimeRange = (
  range: PALTimeRange
): { startDate: moment.Moment; endDate: moment.Moment } => {
  let startDate = moment();
  let endDate = moment();

  switch (range.timeRangeKind) {
    case PickerKinds.PRESET:
      startDate.subtract((range?.timeRangeValue as TimeRangeOption)?.offset, 'minutes');
      break;
    case PickerKinds.ABSOLUTE: {
      const timeRangeValue = range?.timeRangeValue as PALAbsoluteTimeRangeValue;
      startDate = moment(
        `${timeRangeValue.startDate} ${timeRangeValue?.startTime}`,
        'YYYY-MM-DD HH:mm'
      );
      endDate = moment(`${timeRangeValue.endDate} ${timeRangeValue?.endTime}`, 'YYYY-MM-DD HH:mm');
      break;
    }
    case PickerKinds.RELATIVE: {
      const timeRangeValue = range?.timeRangeValue as PALRelativeTimeRangeValue;
      endDate =
        timeRangeValue?.relativeToWhen.toLocaleLowerCase() === 'today'
          ? moment()
          : moment().subtract(1, 'day');
      // set the date
      endDate.hour(Number(timeRangeValue.relativeToTime.split(':')[0]));
      endDate.minute(Number(timeRangeValue.relativeToTime.split(':')[1]));

      startDate = endDate
        .clone()
        .subtract(
          timeRangeValue?.lastNumber,
          timeRangeValue?.lastInterval as moment.unitOfTime.DurationConstructor
        );
      break;
    }
    default:
      break;
  }
  return { startDate, endDate };
};

// Figures out the card data start date, if it comes from the dashboard use that, otherwise use the JSON
export const determineCardDataStartDate = (
  startDateFromDashboard: moment.Moment,
  endDate: moment.Moment,
  selectedTimeRange: PALTimeRange,
  cardRange: Range,
  cardTimeRange: string
): moment.Moment => {
  const presetTimeRange = selectedTimeRange as PALPresetTimeRange;
  // ifthe card is not supposed to listen to the dashboard and a preset has been selected start date should come from the preset
  if (
    shouldCardIgnoreDashboard(cardRange, cardTimeRange) &&
    presetTimeRange?.timeRangeKind === PickerKinds.PRESET &&
    presetTimeRange?.timeRangeValue?.id
  ) {
    return determineStartDateFromPresetRangeChange(
      endDate,
      presetTimeRange.timeRangeValue.id as TimeRanges,
      presetTimeRange.timeRangeValue.offset
    );
  }
  // now check if the card has a hardcoded timeRange
  return cardTimeRange
    ? determineStartDateFromPresetRangeChange(endDate, cardTimeRange as TimeRanges)
    : // If we have a card dataSource.Range, always calculate the start date from the card JSON
    cardRange
    ? determineStartDate(cardRange, endDate)
    : // only listen to the dashboard start date if we don't have JSON specified
      startDateFromDashboard ||
      // if no start date is passed from the dashboard then just assume 7 days
      endDate.clone().subtract(7, 'days');
};

/**
 * Examines
 * @param cardGrain
 * @param timeGrainFromDashboard
 * @returns
 */
export const determineCardDataGrain = (cardGrain: string, timeGrainFromDashboard: string): string =>
  // only use the grain if it changed and is larger than card grain
  timeGrainFromDashboard && cardGrain
    ? compareGrains(cardGrain, timeGrainFromDashboard) < 0
      ? // dashboard grain should override?
        timeGrainFromDashboard
      : // we should revert back to original card grain
        cardGrain
    : undefined;
